// Routing puro del «Dominio propio» (custom domain) de un merchant. Sin env, sin
// IO — el proxy hace el lookup a la DB y solo ejecuta la decisión.
//
// Un dominio propio (empanadafactorytx.com) sirve UNA cosa: la página pública
// del negocio (pedidos o venue). La raíz reescribe a /order/<slug> (o
// /venue/<slug>) y la URL se queda en el dominio del dueño. Todo lo demás:
//   - lo que la propia página pide (/api, /_next, assets, recibo, subrutas de
//     /order/<slug>) pasa tal cual;
//   - las páginas de Redy (login, admin, marketing, legales) van 308 a
//     useredy.com — en este host NO hay CSP (ver next.config.ts), así que aquí
//     no se renderiza nada que no sea la tienda;
//   - cualquier otra ruta (las viejas del sitio anterior: /menu, /about,
//     /contact…) va 301 a la raíz, para no dejar 404 en Google ni en los links
//     que el dueño ya repartió.
import { isConsumerOnlyPath, CONSUMER_APEX } from "@/lib/site/consumerHost"

export const MERCHANT_BASE = "https://useredy.com"

/** Dominios de Redy: nunca pueden ser el «dominio propio» de un merchant. */
const REDY_OWNED = ["useredy.com", "redytoeat.com", "dallasvibra.com", "redypos.com", "redyrides.com", "vercel.app"]

// Hostname: labels of a-z0-9/hyphen, 2+ labels, TLD 2+ alpha. Lowercased.
const DOMAIN_RE = /^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))*\.[a-z]{2,}$/

/**
 * Normaliza lo que escribe el dueño a un hostname. Acepta URL pegada
 * («https://www.mirestaurante.com/menu»), mayúsculas y punto final. Quita el
 * `www.`: el dominio que se guarda es el apex, y el www se agrega en Vercel
 * como redirect a él. Devuelve null si no es un dominio válido o es de Redy.
 */
export function normalizeCustomDomain(raw: string): string | null {
  let d = raw.trim().toLowerCase()
  d = d.replace(/^https?:\/\//, "").replace(/[/?#].*$/, "").replace(/:\d+$/, "").replace(/\.$/, "")
  if (d.startsWith("www.") && d.split(".").length >= 3) d = d.slice("www.".length)
  if (!DOMAIN_RE.test(d)) return null
  if (REDY_OWNED.some((own) => d === own || d.endsWith(`.${own}`))) return null
  return d
}

/**
 * Hosts de la casa merchant: donde vive el admin y rige la CSP estricta.
 * Espejo de MERCHANT_HOST_RE en next.config.ts — hay test que los compara.
 * localhost y las IPs cubren el dev y las tablets de prueba en la LAN.
 */
export function isMerchantPrimaryHost(host: string): boolean {
  const h = host.split(":")[0].toLowerCase()
  if (!h || h === "localhost") return true
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(h)) return true
  if (h === "useredy.com" || h.endsWith(".useredy.com")) return true
  return /^[a-z0-9-]+\.vercel\.app$/.test(h)
}

/** Hosts a buscar en `locations.custom_domain` (el www cae al apex guardado). */
export function customDomainCandidates(host: string): string[] {
  const h = host.split(":")[0].toLowerCase()
  return h.startsWith("www.") ? [h, h.slice("www.".length)] : [h]
}

export type CustomDomainSite = { slug: string; target: "order" | "venue" }

export type CustomDomainDecision =
  | { kind: "pass" }
  | { kind: "rewrite"; pathname: string }
  | { kind: "redirect"; location: string; status: 301 | 308 }
  /** Hace falta saber qué negocio vive en este host (lookup a la DB). */
  | { kind: "lookup" }

// Lo que la página pública pide a su mismo origen (medido sobre
// components/microsite/**: fetch a /api/*, next/image, el recibo del éxito).
const PASS_PREFIXES = ["/api/", "/_next/", "/receipt/", "/r/", "/re/", "/.well-known/"]
const PASS_EXACT = new Set(["/api", "/opengraph-image", "/icon.png", "/apple-icon.png", "/favicon.ico"])

// Raíces de páginas de Redy (no del negocio): van a useredy.com, donde rige
// la CSP. OJO: NO se usa RESERVED_SLUGS — incluye "menu", "about" y
// "contact", que son justo las rutas viejas de un sitio anterior.
const REDY_ROOTS = new Set([
  "login", "signup", "forgot-password", "set-password", "reset-password", "verify-email",
  "auth", "auth-link", "callback", "superadmin", "employee", "tablet", "onboarding",
  "nuevo-negocio", "contador", "home", "me", "demo", "help", "blog", "features",
  "solution", "pricing", "hardware", "design-system", "privacy", "terms", "refund",
  "merchant-agreement", "sms-terms", "sms-opt-in", "sms-account-alerts", "delete-my-account", "unsubscribed",
  "venezuela", "display", "door", "marca", "checkout", "review", "reserva", "m",
  "retail-preview",
])

/**
 * Primera pasada, sin saber qué negocio vive en el host: resuelve lo que no
 * depende del slug y así el lookup a la DB solo corre para páginas.
 */
export function resolveCustomDomainPrecheck(pathname: string): CustomDomainDecision {
  if (pathname === "/robots.txt") return { kind: "rewrite", pathname: "/domain-robots.txt" }
  if (pathname === "/sitemap.xml") return { kind: "rewrite", pathname: "/domain-sitemap.xml" }
  if (PASS_EXACT.has(pathname) || PASS_PREFIXES.some((p) => pathname.startsWith(p))) return { kind: "pass" }
  // Assets sueltos de public/ (/redy-logo.png, /pizza-fotos/*.png) y los
  // manifest. Un .html viejo del sitio anterior sí se trata como página.
  if (pathname.includes(".") && !/\.html?$/.test(pathname)) return { kind: "pass" }
  if (isConsumerOnlyPath(pathname)) {
    // La cuenta y el seguimiento viven en el apex consumer (cookie host-only).
    return { kind: "redirect", location: `https://${CONSUMER_APEX}${pathname}`, status: 308 }
  }
  const first = pathname.split("/").filter(Boolean)[0]
  if (first && REDY_ROOTS.has(first)) {
    return { kind: "redirect", location: `${MERCHANT_BASE}${pathname}`, status: 308 }
  }
  return { kind: "lookup" }
}

/**
 * Segunda pasada, con el negocio del host (o null si el host no es de nadie:
 * dominio desconectado, typo de DNS). Sin negocio, todo va a useredy.com —
 * nunca se renderiza el admin en un host sin CSP.
 */
export function resolveCustomDomainRoute(
  pathname: string,
  site: CustomDomainSite | null
): CustomDomainDecision {
  const pre = resolveCustomDomainPrecheck(pathname)
  if (pre.kind !== "lookup") return pre
  if (!site) return { kind: "redirect", location: `${MERCHANT_BASE}${pathname}`, status: 308 }

  const home = `/${site.target}/${site.slug}`
  if (pathname === "/" || pathname === "") return { kind: "rewrite", pathname: home }

  const segments = pathname.split("/").filter(Boolean)
  // /order/<slug> y /venue/<slug> colapsan a la raíz: un solo canónico por host.
  if (pathname === home || pathname === `${home}/`) return { kind: "redirect", location: "/", status: 308 }
  // Subrutas de la propia página (política de reembolso, mesa, /buy de eventos).
  if (pathname.startsWith(`${home}/`)) return { kind: "pass" }
  if (site.target === "venue" && (segments[0] === "buy" || segments[0] === "guestlist")) return { kind: "pass" }
  // Catering del negocio: página pública hermana del microsite.
  if (segments[0] === site.slug && segments[1] === "catering") return { kind: "pass" }
  // Admin del negocio, otros negocios y las superficies de Redy: a useredy.com.
  if (segments[0] === site.slug || segments[0] === "order" || segments[0] === "venue" ||
      segments[0] === "buy" || segments[0] === "guestlist") {
    return { kind: "redirect", location: `${MERCHANT_BASE}${pathname}`, status: 308 }
  }
  // Rutas viejas del sitio anterior (/menu, /about, /contact, /index.html…).
  return { kind: "redirect", location: "/", status: 301 }
}
